import { Link } from 'react-router-dom';
import type { ColumnDef, RowAction, BulkAction } from '../../components/shared/DataTableTypes';
import { timeAgo } from '../../lib/utils';
import { canOpenProjectSettings } from '../../components/projects/settings/projectSettingsTabs';
import { canWriteProject } from '../../lib/projectPermissions';

export type Project = {
  id: string;
  name: string;
  slug: string;
  role?: string | null;
  statusCounts?: Record<string, number> | null;
  createdAt: string;
};

/**
 * Archive/Delete are owner-only server-side (`requireProjectOwnerForRow` →
 * 403 "Only the project owner can do this"). A missing role fails open —
 * the server still gates, and legacy cached rows without a role must not
 * hide actions from real owners.
 */
export function isProjectOwner(project: { role?: string | null }): boolean {
  return (project.role ?? 'owner') === 'owner';
}

/** Why Settings is disabled for a viewer: the server refuses every project write to them (requireProjectWrite). */
export const SETTINGS_VIEW_ONLY = 'You have view-only access to this project';
const ARCHIVE_OWNER_ONLY = 'Only the project owner can archive this project';
const DELETE_OWNER_ONLY = 'Only the project owner can delete this project';
const UNARCHIVE_OWNER_ONLY = 'Only the project owner can unarchive this project';

function StatusCount({ count, color }: { count: number; color: string }) {
  return (
    <span className={`text-sm ${count > 0 ? `font-medium ${color}` : 'text-gray-400'}`}>
      {count}
    </span>
  );
}

/**
 * The one destination for a project, called by BOTH the name link below and
 * ProjectsPage's row click, so the two cannot drift apart. Mirrors
 * `issuePath` in ../issues/issuesPageColumns.tsx.
 */
export function projectPath(project: Pick<Project, 'id'>): string {
  return `/projects/${project.id}/issues`;
}

export const projectColumns: ColumnDef<Project>[] = [
  {
    header: 'Project',
    render: (project) => (
      <Link to={projectPath(project)} className="group block max-w-xs">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600 truncate transition-colors">{project.name}</span>
          {project.role === 'collaborator' && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 shrink-0">
              Shared
            </span>
          )}
          {project.role === 'viewer' && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200 shrink-0">
              View only
            </span>
          )}
          {project.role === 'team' && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200 shrink-0">
              Team
            </span>
          )}
        </span>
      </Link>
    ),
  },
  {
    header: 'Open',
    className: 'hidden sm:table-cell',
    render: (project) => {
      const count = project.statusCounts?.open ?? 0;
      return <StatusCount count={count} color="text-gray-600" />;
    },
  },
  {
    header: 'In Progress',
    className: 'hidden md:table-cell',
    render: (project) => {
      const count = project.statusCounts?.['in-progress'] ?? 0;
      return <StatusCount count={count} color="text-[var(--color-status-in-progress-text)]" />;
    },
  },
  {
    header: 'Closed',
    className: 'hidden md:table-cell',
    render: (project) => {
      const count = project.statusCounts?.closed ?? 0;
      return <StatusCount count={count} color="text-gray-600" />;
    },
  },
  {
    header: 'Created',
    className: 'hidden lg:table-cell',
    render: (project) => {
      const created = new Date(project.createdAt);
      return (
        <span className="text-sm text-gray-500">
          <time dateTime={created.toISOString()} title={created.toLocaleString()}>
            {timeAgo(project.createdAt)}
          </time>
        </span>
      );
    },
  },
];

export function buildProjectRowActions(opts: {
  onOpenSettings?: (id: string) => void;
  onLiveReview?: (id: string) => void;
  /** "Capture a different page": the URL prompt (see ProjectCard). Same position as the card menu. */
  onLiveReviewOther?: (id: string) => void;
  onClientReview?: (id: string) => void;
  bulkArchive: { mutateAsync: (args: { ids: string[] }) => Promise<unknown> };
  setConfirmDelete: (v: { ids: string[] } | null) => void;
}): RowAction<Project>[] {
  const { onOpenSettings, onLiveReview, onLiveReviewOther, onClientReview, bulkArchive, setConfirmDelete } = opts;
  const actions: RowAction<Project>[] = [];

  if (onOpenSettings) {
    actions.push({
      label: 'Settings',
      onClick: (project) => onOpenSettings(project.id),
      disabled: (project) => !canOpenProjectSettings(project.role),
      disabledTitle: SETTINGS_VIEW_ONLY,
    });
  }
  if (onLiveReview) {
    actions.push({
      label: 'Capture issues',
      hidden: (project) => !canWriteProject(project.role),
      onClick: (project) => onLiveReview(project.id),
    });
  }
  if (onLiveReviewOther) {
    actions.push({
      label: 'Capture a different page',
      hidden: (project) => !canWriteProject(project.role),
      onClick: (project) => onLiveReviewOther(project.id),
    });
  }
  if (onClientReview) {
    actions.push({
      label: 'Share client link',
      hidden: (project) => !canWriteProject(project.role),
      onClick: (project) => onClientReview(project.id),
    });
  }

  actions.push(
    {
      label: 'Archive',
      // Failure is surfaced by the mutation's onError toast; the catch only
      // stops the rejection from escaping as an unhandled $exception.
      onClick: (project) => { bulkArchive.mutateAsync({ ids: [project.id] }).catch(() => {}); },
      disabled: (project) => !isProjectOwner(project),
      disabledTitle: ARCHIVE_OWNER_ONLY,
    },
    {
      label: 'Delete',
      onClick: (project) => setConfirmDelete({ ids: [project.id] }),
      destructive: true,
      disabled: (project) => !isProjectOwner(project),
      disabledTitle: DELETE_OWNER_ONLY,
    },
  );

  return actions;
}

export function buildProjectBulkActions(opts: {
  bulkArchive: { mutateAsync: (args: { ids: string[] }) => Promise<unknown> };
  setConfirmDelete: (v: { ids: string[] } | null) => void;
}): BulkAction<Project>[] {
  const { bulkArchive, setConfirmDelete } = opts;
  return [
    {
      label: 'Archive',
      onClick: (ids) => { bulkArchive.mutateAsync({ ids }).catch(() => {}); },
    },
    {
      label: 'Delete',
      onClick: (ids) => setConfirmDelete({ ids }),
      destructive: true,
    },
  ];
}

/**
 * Row/bulk actions for the "Archived" view. There was previously no way to
 * reverse an archive from the product at all (bulk-archive shipped with no
 * unarchive route or UI, 2026-07-21 things_to_fix). Delete stays reachable
 * here too — an archived project is otherwise permanently unreachable to
 * clean up, since it never appears in the default (non-archived) list.
 */
export function buildArchivedProjectRowActions(opts: {
  bulkUnarchive: { mutateAsync: (args: { ids: string[] }) => Promise<unknown> };
  setConfirmDelete: (v: { ids: string[] } | null) => void;
}): RowAction<Project>[] {
  const { bulkUnarchive, setConfirmDelete } = opts;
  return [
    {
      label: 'Unarchive',
      onClick: (project) => { bulkUnarchive.mutateAsync({ ids: [project.id] }).catch(() => {}); },
      disabled: (project) => !isProjectOwner(project),
      disabledTitle: UNARCHIVE_OWNER_ONLY,
    },
    {
      label: 'Delete',
      onClick: (project) => setConfirmDelete({ ids: [project.id] }),
      destructive: true,
      disabled: (project) => !isProjectOwner(project),
      disabledTitle: DELETE_OWNER_ONLY,
    },
  ];
}

export function buildArchivedProjectBulkActions(opts: {
  bulkUnarchive: { mutateAsync: (args: { ids: string[] }) => Promise<unknown> };
  setConfirmDelete: (v: { ids: string[] } | null) => void;
}): BulkAction<Project>[] {
  const { bulkUnarchive, setConfirmDelete } = opts;
  return [
    {
      label: 'Unarchive',
      onClick: (ids) => { bulkUnarchive.mutateAsync({ ids }).catch(() => {}); },
    },
    {
      label: 'Delete',
      onClick: (ids) => setConfirmDelete({ ids }),
      destructive: true,
    },
  ];
}
